import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { z } from 'zod'
import { setCsrfToken } from '@/api/http/interceptors'
import { useSessionStore } from '@/stores/useSessionStore'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { PasswordVisibilityIcon } from '@/shared/lib/password-visibility'
import brushfireLogo from '@/features/auth/assets/login/brushfire-logo.png'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useToast } from '@/shared/toast/ToastProvider'
import { captureSpAccountActivationNotificationEngaged } from '@/lib/analytics'
import { useApplyForceReset } from '../api'
import {
  evaluateSpPasswordRules,
  allSpPasswordRulesMet,
  FORCE_RESET_FROM_STATE_KEY,
  persistForceResetLoginHandoff,
  isPasswordSaveDeliveryFailure,
  resolveInlinePasswordSaveError,
  resolvePasswordSaveFailureMessage,
} from '../lib'
import { SP_FORCE_RESET_LOGIN_SUCCESS_MESSAGE } from '../lib/password-messages'
import { PasswordChecklist } from '../components'
import {
  RESET_PASSWORD_MISMATCH_MSG,
  resetPasswordCardSx,
  resetPasswordConfirmFieldBlockSx,
  resetPasswordConfirmWithErrorBlockSx,
  resetPasswordErrorInputSx,
  resetPasswordFieldErrorSx,
  resetPasswordFieldLabelSx,
  resetPasswordFormSx,
  resetPasswordHeadingFrameSx,
  resetPasswordHeadingSx,
  resetPasswordInputSx,
  resetPasswordLogoBlockSx,
  resetPasswordPasswordFieldBlockSx,
  resetPasswordPrimaryButtonSx,
  resetPasswordSubtitleSx,
  resetPasswordTitleBlockSx,
} from '../styles/resetPasswordStyles'

const forceResetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(1, 'Password is required')
      .refine(
        (val) => allSpPasswordRulesMet(evaluateSpPasswordRules(val)),
        'Password does not meet the requirements'
      ),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: RESET_PASSWORD_MISMATCH_MSG,
    path: ['confirmPassword'],
  })

type ForceResetPasswordFormData = z.infer<typeof forceResetPasswordSchema>

function RequiredLabel({
  htmlFor,
  children,
}: {
  readonly htmlFor: string
  readonly children: string
}) {
  return (
    <Typography component="label" htmlFor={htmlFor} sx={resetPasswordFieldLabelSx}>
      {children}{' '}
      <Box component="span" className="reset-password-required">
        *
      </Box>
    </Typography>
  )
}

/** BFP-264 — Force password reset for migrated Signal Provider users (TC-SPW2-13 through TC-SPW2-18) */
export function ForceResetPasswordPage() {
  useDocumentTitle('Create New Password')
  const location = useLocation()
  const state = (location.state as Record<string, unknown> | null) ?? {}
  const from = state[FORCE_RESET_FROM_STATE_KEY] as
    | { pathname?: string; search?: string }
    | null
    | undefined

  const applyForceReset = useApplyForceReset()
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ForceResetPasswordFormData>({
    resolver: zodResolver(forceResetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const watchedPassword = watch('password')
  const confirmMismatch =
    !!errors.confirmPassword?.message &&
    errors.confirmPassword.message === RESET_PASSWORD_MISMATCH_MSG

  const passwordReg = register('password')
  const confirmReg = register('confirmPassword')

  const onSubmit = (data: ForceResetPasswordFormData) => {
    setServerError(null)
    applyForceReset.mutate(
      { newPassword: data.password, confirmPassword: data.confirmPassword },
      {
        onSuccess: () => {
          const spUserId = useSessionStore.getState().user?.id
          if (spUserId) {
            captureSpAccountActivationNotificationEngaged(spUserId)
          }
          persistForceResetLoginHandoff(from)
          showToast(SP_FORCE_RESET_LOGIN_SUCCESS_MESSAGE, 'success')
          // Apply bumps tokenVersion — OTP cookies are invalid. Clear client state so
          // useMe does not refetch /me before RequireAuth redirects to /login.
          setCsrfToken(null)
          useSessionStore.getState().clearSession()
          queryClient.clear()
        },
        onError: (error) => {
          if (isPasswordSaveDeliveryFailure(error)) {
            showToast(resolvePasswordSaveFailureMessage(error), 'error')
            return
          }
          setServerError(resolveInlinePasswordSaveError(error))
        },
      }
    )
  }

  return (
    <Box data-testid="force-reset-password-page" sx={resetPasswordCardSx}>
      <Box sx={resetPasswordLogoBlockSx}>
        <Box
          component="img"
          src={brushfireLogo}
          alt="Brushfire"
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>

      <Box sx={resetPasswordTitleBlockSx} data-testid="force-reset-password-title-block">
        <Box sx={resetPasswordHeadingFrameSx}>
          <Typography component="h1" sx={resetPasswordHeadingSx}>
            Create New Password
          </Typography>
        </Box>
        <Typography component="p" sx={resetPasswordSubtitleSx}>
          Please set a new password for your account.
        </Typography>
      </Box>

      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        sx={resetPasswordFormSx}
      >
        <Box sx={resetPasswordPasswordFieldBlockSx}>
          <RequiredLabel htmlFor="force-reset-new-password">Password</RequiredLabel>
          <OutlinedInput
            id="force-reset-new-password"
            data-testid="force-reset-password-new-field"
            type={showNew ? 'text' : 'password'}
            placeholder="Enter password"
            fullWidth
            autoComplete="new-password"
            disabled={applyForceReset.isPending}
            error={!!errors.password}
            aria-invalid={!!errors.password}
            inputRef={passwordReg.ref}
            name={passwordReg.name}
            onChange={passwordReg.onChange}
            onBlur={passwordReg.onBlur}
            sx={{
              ...resetPasswordInputSx,
              ...(errors.password ? resetPasswordErrorInputSx : {}),
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label={showNew ? 'Hide password' : 'Show password'}
                  onClick={() => setShowNew((v) => !v)}
                  edge="end"
                >
                  <PasswordVisibilityIcon visible={showNew} size={24} />
                </IconButton>
              </InputAdornment>
            }
          />
        </Box>

        <Box
          sx={
            confirmMismatch
              ? resetPasswordConfirmWithErrorBlockSx
              : resetPasswordConfirmFieldBlockSx
          }
        >
          <RequiredLabel htmlFor="force-reset-confirm-password">Confirm Password</RequiredLabel>
          <OutlinedInput
            id="force-reset-confirm-password"
            data-testid="force-reset-password-confirm-field"
            type={showConfirm ? 'text' : 'password'}
            placeholder="Re-enter password"
            fullWidth
            autoComplete="new-password"
            disabled={applyForceReset.isPending}
            error={confirmMismatch || !!errors.confirmPassword}
            aria-invalid={confirmMismatch || !!errors.confirmPassword}
            inputRef={confirmReg.ref}
            name={confirmReg.name}
            onChange={confirmReg.onChange}
            onBlur={confirmReg.onBlur}
            sx={{
              ...resetPasswordInputSx,
              ...(confirmMismatch || errors.confirmPassword
                ? resetPasswordErrorInputSx
                : {}),
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
                  onClick={() => setShowConfirm((v) => !v)}
                  edge="end"
                >
                  <PasswordVisibilityIcon visible={showConfirm} size={24} />
                </IconButton>
              </InputAdornment>
            }
          />
          {confirmMismatch && (
            <Typography
              role="alert"
              data-testid="force-reset-password-mismatch-error"
              sx={resetPasswordFieldErrorSx}
            >
              {RESET_PASSWORD_MISMATCH_MSG}
            </Typography>
          )}
        </Box>

        <PasswordChecklist password={watchedPassword} />

        {serverError && (
          <Typography
            role="alert"
            data-testid="force-reset-password-server-error"
            sx={{ ...resetPasswordFieldErrorSx, position: 'static' }}
          >
            {serverError}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disableElevation
          disabled={applyForceReset.isPending}
          data-testid="force-reset-password-submit"
          sx={resetPasswordPrimaryButtonSx}
        >
          {applyForceReset.isPending ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Save & Continue'
          )}
        </Button>
      </Box>
    </Box>
  )
}
